/** Typed HTTP error from Widget Edge Gateway helpers. */
export class WidgetApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'WidgetApiError';
    this.code = code;
    this.status = status;
  }
}

/** Normalize unknown throwables into a string for logs / host events. */
export function describeError(error: unknown, fallback: string): string {
  if (error instanceof WidgetApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

/**
 * Sentence-case the first letter of a displayed error
 * (`network error` → `Network error`).
 */
export function capitalizeErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return message;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * True only when the device is offline.
 * Browser `Failed to fetch` also happens when the Gateway is down or the
 * CORS preflight fails — that is a backend issue, not a client network drop.
 */
export function isNetworkFailure(error?: unknown): boolean {
  if (error instanceof WidgetApiError) return false;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Gateway 403 when `widget_status` is flipped off after the panel opened. */
export function isWidgetDisabledError(
  error?: unknown,
  reason?: string,
): boolean {
  const code =
    error instanceof WidgetApiError ? error.code.toLowerCase() : '';
  const text = [
    reason,
    error instanceof Error ? error.message : '',
    code,
  ]
    .join(' ')
    .toLowerCase();
  return (
    text.includes('widget is disabled') ||
    text.includes('widget_disabled') ||
    code === 'widget_disabled'
  );
}

/** Fetch/session cancelled via AbortController — not a user-facing failure. */
export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return error instanceof Error && error.name === 'AbortError';
}
